/**
 * The Business Discovery Report for one session.
 *
 * INTERNAL. Reached by session id, which no client ever holds, and never
 * linked from the client's page. The report is written automatically when a
 * Business Discovery finishes; this page reads it.
 *
 * A read-only summary of what the prospect said — no analytics, no score.
 * Sections that were never established say so plainly rather than being
 * hidden, because the gaps are part of the finding. The data and its structure
 * are the report's own; only the presentation changed.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { documentDownloadUrl, fetchReport, finalizeSession, reportDocxDownloadUrl } from '../api/client';
import { DISCOVERY_LAYERS } from '../config';
import ConsoleLayout from './ConsoleLayout';
import {
  Badge,
  BulletList,
  Card,
  EmptyState,
  ErrorState,
  Facts,
  LoadingState,
  PageHeader,
  Prose,
  SubHeading,
} from './consoleUi';
import { NOT_PROVIDED, REPORT_NOT_AVAILABLE, formatDateTime, formatDuration, friendlyError } from './consoleData';
import '../styles/ReportPage.css';

/**
 * Turn an internal stage code into words.
 *
 * The report says "Deep Dive", not "L6": it is written to be read, and it is
 * the same document that is downloaded and shared as a DOCX.
 */
function stageName(id) {
  if (!id) return null;
  return DISCOVERY_LAYERS.find((layer) => layer.id === id)?.label || id;
}

function InferredTag() {
  return (
    <span className="nsc-tag" title="Derived from what was said, not stated directly">
      Inferred
    </span>
  );
}

/** A fact with its provenance, so an inference is never read as a quote. */
function fact(label, source) {
  return {
    label,
    value: source?.value,
    tag: source?.provenance === 'inferred' ? <InferredTag /> : null,
  };
}

const EVIDENCE_TONES = {
  confirmed: 'ok',
  rejected: 'danger',
  pending: 'warn',
  unresolved: 'warn',
  outdated: 'neutral',
};

export default function ReportPage() {
  const { sessionId } = useParams();
  const [report, setReport] = useState(null);
  const [state, setState] = useState('loading');
  const [error, setError] = useState(null);

  const overviewPath = `/internal/sessions/${sessionId}/discovery`;
  const back = { to: overviewPath, label: 'Business Discovery overview' };

  const load = useCallback(async () => {
    setState('loading');
    setError(null);
    try {
      setReport(await fetchReport(sessionId));
      setState('ready');
    } catch (cause) {
      if (cause.status === 404) {
        setState('absent');
      } else {
        setError(friendlyError(cause));
        setState('error');
      }
    }
  }, [sessionId]);

  useEffect(() => { load(); }, [load]);

  /**
   * Run finalisation for this session by hand.
   *
   * INTERNAL REPAIR ONLY, and not how reports are normally made: a Business
   * Discovery finalises itself when the voice agent leaves the room, which is
   * why the client never presses anything. This takes the same idempotent
   * path, so a session that already has a report keeps the one it has, and a
   * conversation that never reached its close is refused rather than
   * half-reported.
   */
  const build = async () => {
    setState('generating');
    setError(null);
    try {
      const outcome = await finalizeSession(sessionId);
      if (outcome.status === 'not_ready') {
        setError(
          'This Business Discovery did not reach its close, so there is nothing to report on yet.',
        );
        setState('absent');
        return;
      }
      await load();
    } catch (cause) {
      setError(friendlyError(cause));
      setState('error');
    }
  };

  if (state === 'loading' || state === 'generating') {
    return (
      <ConsoleLayout title="Business Discovery Report">
        <PageHeader back={back} eyebrow="Business Discovery Report" title="Business Discovery Report" />
        <Card>
          {state === 'loading' ? (
            <LoadingState label="Loading the report…" />
          ) : (
            <LoadingState label="Generating the report from the conversation. This can take a minute." />
          )}
        </Card>
      </ConsoleLayout>
    );
  }

  if (state === 'absent' || state === 'error') {
    return (
      <ConsoleLayout title="Business Discovery Report">
        <PageHeader
          back={back}
          eyebrow="Business Discovery Report"
          title="Business Discovery Report"
          subtitle={<span className="nsc-mono">Reference {sessionId}</span>}
        />
        <Card>
          {state === 'absent' ? (
            <EmptyState
              title={REPORT_NOT_AVAILABLE}
              action={
                <button type="button" className="nsc-button nsc-button--secondary" onClick={build}>
                  Run finalisation
                </button>
              }
            >
              {error || 'No report has been generated for this Business Discovery yet.'}
            </EmptyState>
          ) : (
            <ErrorState
              title="The report could not be loaded"
              action={
                <button type="button" className="nsc-button nsc-button--secondary" onClick={build}>
                  Run finalisation
                </button>
              }
            >
              {error}
            </ErrorState>
          )}
        </Card>
      </ConsoleLayout>
    );
  }

  const r = report;
  const meta = r.interview_metadata || {};
  const attached = r.documents?.attached || [];
  const evidence = r.documents?.evidence || [];
  const organisation = r.prospect?.organization?.value;

  return (
    <ConsoleLayout title="Business Discovery Report">
      <PageHeader
        back={back}
        eyebrow="Business Discovery Report"
        title={organisation && organisation !== NOT_PROVIDED ? organisation : 'Company not provided'}
        subtitle={<span className="nsc-mono">Reference {r.session_id}</span>}
        actions={
          <>
            <a href={reportDocxDownloadUrl(sessionId)} download className="nsc-button nsc-button--primary">
              Download DOCX
            </a>
            <Link to={`/internal/sessions/${sessionId}/transcript`} className="nsc-button nsc-button--secondary">
              Transcript
            </Link>
          </>
        }
      >
        <div className="nsc-header-badges">
          {r.interview_complete ? (
            <Badge tone="ok">Business Discovery complete</Badge>
          ) : (
            <Badge tone="warn">Discovery incomplete</Badge>
          )}
          <span className="nsc-header-meta">Generated {formatDateTime(r.generated_at)}</span>
        </div>
      </PageHeader>

      {!r.interview_complete && (
        <p className="nsc-alert nsc-alert--warn">
          Discovery incomplete: the conversation did not finish with a confirmed problem.
        </p>
      )}

      <Card title="Executive summary" className="nsc-card--highlight">
        <Prose text={r.executive_summary} />
      </Card>

      <div className="nsc-report-grid">
        <Card title="Prospect">
          <Facts
            items={[
              fact('Contact', r.prospect?.contact_name),
              fact('Role', r.prospect?.role),
              fact('Organisation', r.prospect?.organization),
            ]}
          />
        </Card>

        <Card title="Industry">
          <Facts items={[fact('Industry', r.industry?.industry), fact('Vertical', r.industry?.vertical)]} />
        </Card>
      </div>

      <Card title="Business">
        <Prose text={r.business?.overview} />
        <div className="nsc-two-col">
          <div>
            <SubHeading>Products and services</SubHeading>
            <BulletList items={r.business?.products_services} />
          </div>
          <div>
            <SubHeading>Customers</SubHeading>
            <BulletList items={r.business?.target_customers} />
          </div>
        </div>
      </Card>

      <Card title="Current technology and process">
        <Prose text={r.technology_environment?.summary} />
        <div className="nsc-two-col">
          <div>
            <SubHeading>Tools in use</SubHeading>
            <BulletList items={r.technology_environment?.tools} />
          </div>
          <div>
            <SubHeading>Manual processes</SubHeading>
            <BulletList items={r.technology_environment?.manual_processes} />
          </div>
        </div>
      </Card>

      <Card title="Identified problems">
        {!r.pain_points || r.pain_points.length === 0 ? (
          <p className="nsc-empty-value">No problem was described during the conversation.</p>
        ) : (
          <ul className="nsc-pains">
            {r.pain_points.map((pain, index) => (
              <li key={index} className="nsc-pain">
                <p className="nsc-pain__title">{pain.problem}</p>
                <div className="nsc-pain__badges">
                  {pain.confirmed && <Badge tone="ok">Confirmed</Badge>}
                  {!pain.specific && <Badge tone="neutral">General</Badge>}
                </div>
                {pain.evidence && <blockquote className="nsc-quote">“{pain.evidence}”</blockquote>}
                {(pain.frequency || pain.affected_teams?.length > 0 || pain.current_workaround) && (
                  <dl className="nsc-pain__meta">
                    {pain.frequency && (
                      <div><dt>Frequency</dt><dd>{pain.frequency}</dd></div>
                    )}
                    {pain.affected_teams?.length > 0 && (
                      <div><dt>Affected</dt><dd>{pain.affected_teams.join(', ')}</dd></div>
                    )}
                    {pain.current_workaround && (
                      <div><dt>Workaround</dt><dd>{pain.current_workaround}</dd></div>
                    )}
                  </dl>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card title="Deep-dive findings">
        <Prose text={r.deep_dive?.summary} />
        <Facts
          items={[
            { label: 'Workflow', value: r.deep_dive?.workflow },
            { label: 'Frequency', value: r.deep_dive?.frequency },
            { label: 'Scale', value: r.deep_dive?.scale },
            { label: 'Root cause', value: r.deep_dive?.root_cause },
            { label: 'Workaround', value: r.deep_dive?.current_workaround },
            { label: 'Urgency', value: r.deep_dive?.urgency },
          ]}
        />
      </Card>

      <Card title="Business impact">
        <Prose text={r.business_impact?.summary} />
        <Facts
          items={[
            { label: 'Operational', value: r.business_impact?.operational_impact },
            { label: 'Time', value: r.business_impact?.time_impact },
            { label: 'Cost', value: r.business_impact?.cost_impact },
            { label: 'Customer', value: r.business_impact?.customer_impact },
          ]}
        />
      </Card>

      <div className="nsc-report-grid">
        <Card title="Desired outcomes">
          <BulletList items={r.desired_outcomes} empty="No desired outcome was stated." />
        </Card>

        <Card title="Confirmed problem statement">
          <div className="nsc-statement">
            {r.confirmed_problem?.statement && r.confirmed_problem.statement !== NOT_PROVIDED ? (
              <blockquote className={`nsc-quote nsc-quote--large${r.confirmed_problem?.confirmed ? '' : ' nsc-quote--muted'}`}>
                “{r.confirmed_problem.statement}”
              </blockquote>
            ) : (
              <p className="nsc-empty-value">{NOT_PROVIDED}</p>
            )}
            {r.confirmed_problem?.confirmed ? (
              <Badge tone="ok">Confirmed by the client</Badge>
            ) : (
              <Badge tone="warn">Not confirmed</Badge>
            )}
          </div>
        </Card>
      </div>

      <Card title="Documents">
        <Facts
          items={[
            {
              label: 'Documents available',
              value:
                r.documents?.available === null || r.documents?.available === undefined
                  ? null
                  : r.documents.available ? 'Yes' : 'No',
            },
          ]}
        />
        {r.documents?.available && (
          <>
            <SubHeading>Types</SubHeading>
            <BulletList items={r.documents.types} empty="Type not specified." />
          </>
        )}

        {/* Files actually attached to the session. Listed as supporting
            material — nothing in them is a finding of the conversation. */}
        {attached.length > 0 && (
          <>
            <SubHeading>Attached files</SubHeading>
            <ul className="nsc-file-list">
              {attached.map((doc) => (
                <li key={doc.document_id} className="nsc-file">
                  <div className="nsc-file__main">
                    <span className="nsc-file__name">{doc.filename}</span>
                    <span className="nsc-file__meta">
                      {doc.status}
                      {doc.extracted_text_available ? ' · Text extracted' : ''}
                    </span>
                  </div>
                  <a
                    className="nsc-button nsc-button--ghost"
                    href={documentDownloadUrl(r.session_id, doc.document_id)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    View
                  </a>
                </li>
              ))}
            </ul>
          </>
        )}

        {/* What a document said, and what the client answered when asked about
            it. The client's answer is what settles it. */}
        {evidence.length > 0 && (
          <>
            <SubHeading>Document evidence</SubHeading>
            <ul className="nsc-evidence">
              {evidence.map((item, index) => (
                <li key={`${item.filename}-${index}`} className="nsc-evidence__item">
                  <div className="nsc-evidence__head">
                    <span className="nsc-evidence__file">{item.filename}</span>
                    <Badge tone={EVIDENCE_TONES[item.status] || 'neutral'}>{item.status}</Badge>
                  </div>
                  <blockquote className="nsc-quote">“{item.quote}”</blockquote>
                  {item.conflicted_with && (
                    <p className="nsc-evidence__meta">The client had said “{item.conflicted_with}”</p>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}

        {r.documents?.notes && r.documents.notes !== NOT_PROVIDED && (
          <p className="nsc-card__note">{r.documents.notes}</p>
        )}
      </Card>

      <div className="nsc-report-grid">
        <Card title="Recommended next step">
          <Prose text={r.recommended_next_step} />
        </Card>

        <Card title="Open questions and missing information">
          <BulletList items={r.open_questions} empty="No gaps recorded." />
        </Card>
      </div>

      <Card title="Session details">
        <Facts
          items={[
            { label: 'Reference', value: meta.session_id },
            { label: 'Status', value: meta.status },
            { label: 'Started', value: formatDateTime(meta.started_at) },
            { label: 'Ended', value: formatDateTime(meta.ended_at) },
            { label: 'Duration', value: formatDuration(meta.duration_seconds) },
            { label: 'Client turns', value: String(meta.turn_count ?? 0) },
            { label: 'Reached', value: stageName(meta.final_level) },
            {
              label: 'Stages covered',
              value: (meta.completed_levels || []).map(stageName).join(', ') || null,
            },
            { label: 'Recording', value: meta.recording_available ? 'Available' : 'Not available' },
          ]}
        />
      </Card>

      {r.generation?.warnings?.length > 0 && (
        <Card title="Notes on this report">
          <BulletList items={r.generation.warnings} />
        </Card>
      )}

      <p className="nsc-footnote">Generated automatically {formatDateTime(r.generated_at)}</p>
    </ConsoleLayout>
  );
}
